import { ContentLabel } from 'components/ContentLabel'
import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import ProfileViewModel from '../ProfileViewModel'
import ProfileHeader from '../ProfileHeader'
import { ScrollOffsetInterpolation } from 'hooks/spring-hooks'
import { useActivation, useTask } from 'hooks/mobx-hooks'
import { useDocumentTitle } from 'hooks/document-title'
import Icon from 'components/Icon'
import Checkbox from 'components/Checkbox'
import ContentLoading from 'components/ContentLoading'
import ErrorBlock from 'components/ErrorBlock'
import Button from 'components/Button'
import Spinner from 'components/Spinner'
import IconVisual from 'components/IconVisual'
import { Dropdown, DropdownItem } from 'components/Dropdown'
import {
  ContentLayout,
  ContentMain,
  ContentTip,
} from 'components/ContentLayout'
import {
  NotificationMedium,
  NotificationType,
} from './ProfileNotificationsViewModel'
import { addHours, formatRelative } from 'date-fns'
import styles from './Notifications.sass'

interface NotificationsProps {
  vm: ProfileViewModel
  scrollOffset: ScrollOffsetInterpolation
}

export default observer(function Notifications({
  vm,
  scrollOffset,
}: NotificationsProps) {
  useActivation(vm.notifications)
  useDocumentTitle('Notification Preferences')
  return (
    <>
      <ProfileHeader
        viewTitle="Notifications"
        vm={vm}
        scrollOffset={scrollOffset}
      />
      <ContentLayout accountForLabel>
        <ContentMain>
          <div className={cx(styles.header)}>
            <HeaderColumn fill>System Notifications</HeaderColumn>
            {vm.notifications.isSnoozingEnabled && (
              <HeaderColumn>Snooze</HeaderColumn>
            )}
            <HeaderColumn>Push</HeaderColumn>
            <HeaderColumn>Email</HeaderColumn>
          </div>
          {vm.notifications.fetchNotificationPreferences.match({
            pending: () => <ContentLoading />,
            rejected: (err) => <ErrorBlock error={err} />,
            resolved: () => (
              <>
                <div className={styles.list}>
                  <Group>
                    <Item
                      type={vm.notifications.master}
                      snoozingOn={vm.notifications.isSnoozingEnabled}
                    />
                  </Group>
                  <Divider />
                  <Group>
                    {vm.notifications.types.map((type) => (
                      <Item
                        type={type}
                        key={type.id}
                        snoozingOn={vm.notifications.isSnoozingEnabled}
                      />
                    ))}
                  </Group>
                </div>
              </>
            ),
          })}
        </ContentMain>
        <ContentTip
          title="Stay In The Loop"
          illustrationUrl={require('assets/images/img_notification-settings.svg')}
        >
          Easily customise how you want to receive job notifications here. For
          more info on how notifications work, visit our help center.
        </ContentTip>
      </ContentLayout>
    </>
  )
})

interface HeaderColumnProps {
  children?: React.ReactNode
  fill?: boolean
}

const HeaderColumn = ({ children, fill }: HeaderColumnProps) => {
  return (
    <div className={cx(styles.headerColumn, fill && styles.fill)}>
      <ContentLabel>{children}</ContentLabel>
    </div>
  )
}

interface GroupProps {
  children: React.ReactNode
}

const Group = ({ children }: GroupProps) => (
  <div className={styles.group}>{children}</div>
)

const Divider = () => <div className={styles.divider}></div>

interface ItemProps {
  type: NotificationType
  snoozingOn?: boolean
}

const Item = observer(function Item({ type, snoozingOn = false }: ItemProps) {
  const anySnoozed = type.isAnyMediumSnoozed()

  return (
    <div className={styles.item}>
      <div className={cx(styles.icon)}>
        <IconVisual muted={anySnoozed}>
          <Icon
            size={20}
            name={
              anySnoozed ? 'notifications-thick-muted' : 'notifications-thick'
            }
          />
        </IconVisual>
      </div>
      <div className={styles.content}>
        <p className={styles.title}>{type.title}</p>
        {type.description && (
          <p className={styles.description}>{type.description}</p>
        )}
      </div>

      {snoozingOn && <SnoozeDropdown type={type} />}

      {[NotificationMedium.Push, NotificationMedium.Email].map((medium, i) => (
        <MediumToggle
          key={i}
          canToggleMedium={type.canToggleMedium}
          isMediumEnabled={type.isEnabled}
          toggleMedium={type.toggleMedium}
          medium={medium}
        />
      ))}
    </div>
  )
})

interface MediumToggleProps {
  medium: NotificationMedium

  toggleMedium(medium: NotificationMedium): Promise<unknown>

  isMediumEnabled(medium: NotificationMedium): boolean

  canToggleMedium(medium: NotificationMedium): boolean
}

const MediumToggle = observer(function MediumToggle({
  medium,
  toggleMedium,
  canToggleMedium,
  isMediumEnabled,
}: MediumToggleProps) {
  const toggle = useTask.resolved(
    () => toggleMedium(medium),
    [toggleMedium, medium],
  )
  return (
    <div className={styles.toggleContainer}>
      <Checkbox
        checked={isMediumEnabled(medium)}
        onChange={toggle}
        loading={toggle.pending}
        disabled={toggle.pending || !canToggleMedium(medium)}
      />
    </div>
  )
})

interface SnoozeDropdownProps {
  type: NotificationType
}

const SnoozeDropdown = observer(function SnoozeDropdown({
  type,
}: SnoozeDropdownProps) {
  const snoozed = type.isAnyMediumSnoozed()

  const snooze = useTask.resolved(
    async (amountHrs: number) => {
      const snoozeUntil = addHours(new Date(), amountHrs)
      await type.snoozeAll(snoozeUntil)
    },
    [type],
  )

  return (
    <div className={styles.snoozeDropdownContainer}>
      <Dropdown
        autoDismiss
        position="bottom-end"
        disabled={!type.canSnooze()}
        button={
          <Button
            className={styles.snoozeDropdownButton}
            type="secondary"
            borderless
            disabled={snooze.pending || !type.canSnooze()}
            iconRight={snooze.pending ? <Spinner /> : <Icon name="dropdown" />}
          >
            {!snoozed && <>Not snoozed</>}
            {snoozed && (
              <>
                Until{' '}
                {formatRelative(
                  type.mediumSnoozeDate(NotificationMedium.Email)!,
                  new Date(),
                )}
              </>
            )}
          </Button>
        }
      >
        {!snoozed && (
          <>
            <DropdownItem onClick={() => snooze(12)}>
              Snooze for 12 Hours
            </DropdownItem>
            <DropdownItem onClick={() => snooze(24)}>
              Snooze for 24 Hours
            </DropdownItem>
          </>
        )}
        {snoozed && (
          <DropdownItem onClick={() => snooze(0)}>Unsnooze</DropdownItem>
        )}
      </Dropdown>
    </div>
  )
})
